import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Badge,
  Fade,
  Slide,
  Zoom,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Assignment,
  Schedule,
  Warning,
  PriorityHigh,
  Visibility,
  Analytics,
  Description,
  ExpandMore,
  Science,
  Person,
  LocationOn,
  AccessTime,
  Comment,
  Download,
  Print,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material/styles';
import samplesService, { Sample, Priority, SampleStatus } from '../services/samplesService';

const Validation: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [validationComment, setValidationComment] = useState('');
  const [validationAction, setValidationAction] = useState<'approve' | 'reject' | null>(null);
  const [stats, setStats] = useState({
    pending_validation: 0,
    validated_today: 0,
    urgent_samples: 0,
    rejected_samples: 0,
  });

  useEffect(() => {
    loadValidationData();
  }, []);

  const loadValidationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load samples pending validation (VALIDATION_SERVICE status) - INCREASED LIMIT
      const pendingResponse = await samplesService.getSamples(1, 200, {
        status: [SampleStatus.VALIDATION_SERVICE],
      });
      
      // All samples with VALIDATION_SERVICE status are ready for validation
      const completedAnalyses = pendingResponse.samples;
      
      // Load validated samples from today (PRET status) - INCREASED LIMIT
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const validatedResponse = await samplesService.getSamples(1, 300, {
        status: [SampleStatus.PRET],
        dateFrom: today,
      });

      // Load rejected samples - INCREASED LIMIT
      const rejectedResponse = await samplesService.getSamples(1, 200, {
        status: [SampleStatus.REJETE],
        dateFrom: today,
      });
      
      setSamples(completedAnalyses);
      
      // Calculate stats from real data
      const urgentPendingSamples = completedAnalyses.filter(
        sample => sample.priorite === Priority.URGENTE
      ).length;
      
      setStats({
        pending_validation: completedAnalyses.length,
        validated_today: validatedResponse.samples.length,
        urgent_samples: urgentPendingSamples,
        rejected_samples: rejectedResponse.samples.length,
      });
    } catch (error) {
      console.error('Error loading validation data:', error);
      setError('Erreur lors du chargement des données de validation');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (action: 'approve' | 'reject') => {
    if (!selectedSample) return;

    try {
      const newStatus = action === 'approve' ? SampleStatus.GENERATION_CERTIFICAT : SampleStatus.REJETE;
      const statusMessage = action === 'approve' 
        ? `Analyse validée: ${validationComment}` 
        : `Analyse rejetée: ${validationComment}`;

      await samplesService.updateSampleStatus(selectedSample.id, newStatus, statusMessage);
      
      // Update sample with validation data
      await samplesService.updateSample(selectedSample.id, {
        metadonnees: {
          ...selectedSample.metadonnees,
          validation_data: {
            action,
            comment: validationComment,
            validator_id: user?.id,
            validator_name: `${user?.firstName} ${user?.lastName}`,
            validation_date: new Date().toISOString(),
          }
        }
      });

      setShowValidationDialog(false);
      setSelectedSample(null);
      setValidationComment('');
      setValidationAction(null);
      loadValidationData();
    } catch (error) {
      console.error('Error validating sample:', error);
      setError(`Erreur lors de la validation de l'échantillon: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  const handleSampleClick = (sample: Sample, action: 'approve' | 'reject') => {
    setSelectedSample(sample);
    setValidationAction(action);
    setShowValidationDialog(true);
  };

  const handleViewDetails = (sample: Sample) => {
    navigate(`/samples/${sample.id}`);
  };

  const handleGenerateCertificate = (sample: Sample) => {
    // Navigate to certificate generation
    navigate(`/results/certificates?sample=${sample.id}`);
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE: return 'error';
      case Priority.ELEVEE: return 'warning';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE: return <PriorityHigh />;
      case Priority.ELEVEE: return <Warning />;
      default: return <Schedule />;
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          borderRadius: 3,
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Chargement des données de validation...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
      minHeight: '100vh',
    }}>
      {/* Modern Header with Glassmorphism */}
      <Fade in timeout={800}>
        <Card sx={{ 
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          borderRadius: 4,
          overflow: 'visible',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: '16px 16px 0 0',
          }
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography 
                  variant="h3" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  Bonjour, {user?.firstName} ✅
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                  Validation des analyses - Contrôle qualité
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<Description />}
                  onClick={() => navigate('/results/certificates')}
                  sx={{ 
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                    borderRadius: 3,
                  }}
                >
                  Certificats
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {error && (
        <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 3,
              backdropFilter: 'blur(10px)',
              background: alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Slide>
      )}

      {/* Modern Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          {
            icon: Assignment,
            value: stats.pending_validation,
            label: 'En attente de validation',
            color: theme.palette.warning.main,
            gradient: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
            delay: 200,
          },
          {
            icon: CheckCircle,
            value: stats.validated_today,
            label: 'Validées aujourd\'hui',
            color: theme.palette.success.main,
            gradient: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            delay: 400,
          },
          {
            icon: PriorityHigh,
            value: stats.urgent_samples,
            label: 'Échantillons urgents',
            color: theme.palette.error.main,
            gradient: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            delay: 600,
          },
          {
            icon: Cancel,
            value: stats.rejected_samples,
            label: 'Rejetées aujourd\'hui',
            color: theme.palette.error.main,
            gradient: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            delay: 800,
          },
        ].map((stat, index) => (
          <Grid item xs={12} md={3} key={index}>
            <Zoom in timeout={stat.delay}>
              <Card sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 4,
                background: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 20px 40px ${alpha(stat.color, 0.3)}`,
                  '& .stat-icon': {
                    transform: 'scale(1.1) rotate(5deg)',
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: stat.gradient,
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography 
                        variant="h3" 
                        component="div" 
                        sx={{ 
                          fontWeight: 700,
                          color: stat.color,
                          mb: 1,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography 
                        color="text.secondary" 
                        variant="body1"
                        sx={{ fontWeight: 500 }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                    <Box
                      className="stat-icon"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 3,
                        background: stat.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <stat.icon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Instructions */}
      <Fade in timeout={1200}>
        <Alert 
          severity="info" 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.light, 0.1)})`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            '& .MuiAlert-icon': {
              color: theme.palette.info.main,
            }
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            <strong>Instructions:</strong> Examinez les résultats d'analyse et validez ou rejetez chaque échantillon. 
            Les échantillons validés pourront ensuite être utilisés pour générer des certificats d'analyse.
          </Typography>
        </Alert>
      </Fade>

      {/* Samples List */}
      <Fade in timeout={1400}>
        <Paper sx={{ 
          borderRadius: 4,
          background: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          overflow: 'hidden',
        }}>
          <Box sx={{ 
            p: 3, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Analytics color="primary" />
              Échantillons en Attente de Validation
              <Chip 
                label={samples.length} 
                color="primary" 
                size="small"
                sx={{ 
                  fontWeight: 600,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              />
            </Typography>
          </Box>
          
          {samples.length === 0 ? (
            <Box textAlign="center" py={8}>
              <CheckCircle sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                Aucun échantillon en attente de validation
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Toutes les analyses ont été validées
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {samples.map((sample, index) => (
                <React.Fragment key={sample.id}>
                  <Zoom in timeout={1600 + index * 100}>
                    <ListItem
                      sx={{ 
                        px: 3,
                        py: 3,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': { 
                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
                          transform: 'translateX(8px)',
                          '& .sample-actions': {
                            opacity: 1,
                            transform: 'translateX(0)',
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: sample.priorite === Priority.URGENTE 
                            ? `linear-gradient(180deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
                            : sample.priorite === Priority.ELEVEE
                            ? `linear-gradient(180deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
                            : `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                          opacity: 0,
                          transition: 'opacity 0.3s ease',
                        },
                        '&:hover::before': {
                          opacity: 1,
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Badge
                          color={getPriorityColor(sample.priorite as Priority)}
                          variant="dot"
                          invisible={sample.priorite === Priority.NORMALE}
                        >
                          <Box sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 3,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <Analytics sx={{ color: 'white', fontSize: 24 }} />
                          </Box>
                        </Badge>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="h6" fontWeight="600">
                              {sample.sample_id}
                            </Typography>
                            <Chip
                              icon={getPriorityIcon(sample.priorite as Priority)}
                              label={sample.priorite}
                              size="small"
                              color={getPriorityColor(sample.priorite as Priority)}
                              sx={{ fontWeight: 500 }}
                            />
                            <Chip
                              label={sample.type_echantillon}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                            <Chip
                              label="Analyse terminée"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <span>
                            <span style={{ display: 'block', marginBottom: '4px', color: 'rgba(0, 0, 0, 0.6)' }}>
                              <strong>Client:</strong> {sample.client?.company_name || 'N/A'}
                            </span>
                            <span style={{ display: 'block', marginBottom: '4px', color: 'rgba(0, 0, 0, 0.6)' }}>
                              <strong>Analyste:</strong> {sample.assigne_a || 'Non assigné'}
                            </span>
                            <span style={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                              <strong>Analyse terminée:</strong> {sample.date_fin_analyse ? new Date(sample.date_fin_analyse).toLocaleDateString('fr-FR') : 'N/A'}
                            </span>
                          </span>
                        }
                      />
                      
                      <Box 
                        className="sample-actions"
                        display="flex" 
                        gap={1}
                        sx={{
                          opacity: 0,
                          transform: 'translateX(20px)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        <Tooltip title="Voir détails" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(sample)}
                            sx={{
                              background: alpha(theme.palette.info.main, 0.1),
                              '&:hover': { background: alpha(theme.palette.info.main, 0.2) }
                            }}
                          >
                            <Visibility color="info" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Rejeter" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleSampleClick(sample, 'reject')}
                            sx={{
                              background: alpha(theme.palette.error.main, 0.1),
                              '&:hover': { background: alpha(theme.palette.error.main, 0.2) }
                            }}
                          >
                            <Cancel color="error" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Valider" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleSampleClick(sample, 'approve')}
                            sx={{
                              background: alpha(theme.palette.success.main, 0.1),
                              '&:hover': { background: alpha(theme.palette.success.main, 0.2) }
                            }}
                          >
                            <CheckCircle color="success" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItem>
                  </Zoom>
                  {index < samples.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Fade>

      {/* Validation Dialog */}
      <Dialog
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            {validationAction === 'approve' ? (
              <CheckCircle color="success" />
            ) : (
              <Cancel color="error" />
            )}
            <Typography variant="h6">
              {validationAction === 'approve' ? 'Valider' : 'Rejeter'} l'échantillon {selectedSample?.sample_id}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Informations échantillon:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Client:</strong> {selectedSample?.client?.company_name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {selectedSample?.type_echantillon}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Priorité:</strong> {selectedSample?.priorite}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Analyste:</strong> {selectedSample?.assigne_a}
                </Typography>
              </Grid>
            </Grid>
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label={validationAction === 'approve' ? 'Commentaires de validation' : 'Raison du rejet'}
            value={validationComment}
            onChange={(e) => setValidationComment(e.target.value)}
            placeholder={
              validationAction === 'approve' 
                ? 'Ajoutez vos commentaires sur la validation...'
                : 'Expliquez pourquoi cette analyse est rejetée...'
            }
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setShowValidationDialog(false)}
            variant="outlined"
          >
            Annuler
          </Button>
          <Button
            onClick={() => handleValidate(validationAction!)}
            color={validationAction === 'approve' ? 'success' : 'error'}
            variant="contained"
            startIcon={validationAction === 'approve' ? <CheckCircle /> : <Cancel />}
            disabled={!validationComment.trim()}
            sx={{
              background: validationAction === 'approve' 
                ? `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                : `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
            }}
          >
            {validationAction === 'approve' ? 'Valider' : 'Rejeter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Validation; 